import {Component, EventEmitter, inject, Input, OnInit, Output, signal} from '@angular/core';
import {finalize} from 'rxjs';
import {ProfileFormComponent} from '../profile/profile-form/profile-form.component';
import {UserService} from '../../../shared/services/user.service';
import {AlertService} from '../../../shared/services/alert.service';
import {Messages} from '../../../shared/constants/messages';
import {User} from '../../../shared/model/user';
import {UserUpdate} from '../../../shared/model/user-update';

/**
 * Modifica di un utente qualsiasi direttamente dalla pagina admin (usata dentro una modale).
 * Riusa il form profilo; il backend consente l'update a ADMINISTRATOR o all'utente stesso.
 */
@Component({
  selector: 'app-admin-user-edit',
  standalone: true,
  imports: [ProfileFormComponent],
  template: `
    @if (userData(); as user) {
      <div class="d-flex align-items-center justify-content-between px-3 py-2 mb-3 bg-light border rounded">
        <span class="small text-muted">Stato utente:</span>
        <div>
          @if (user.userStatus.banned) {
            <span class="badge bg-danger">Bannato</span>
          } @else if (user.userStatus.active) {
            <span class="badge bg-success">Attivo</span>
          } @else if (user.userStatus.emailVerificationPending) {
            <span class="badge bg-warning text-dark">In attesa mail</span>
          } @else {
            <span class="badge bg-secondary">Disattivato</span>
          }
        </div>
      </div>
    }
    <app-profile-form
      [userData]="userData()"
      [isEditing]="true"
      [isLoading]="isLoading()"
      (save)="onSave($event)"
      (cancel)="cancelled.emit()"
    ></app-profile-form>
  `,
})
export class AdminUserEditComponent implements OnInit {
  private userService = inject(UserService);
  private alertService = inject(AlertService);

  @Input({required: true}) userId!: number;
  @Output() saved = new EventEmitter<User>();
  @Output() cancelled = new EventEmitter<void>();

  protected userData = signal<User | undefined>(undefined);
  protected isLoading = signal(false);

  ngOnInit(): void {
    this.isLoading.set(true);
    this.userService
      .getUserById(this.userId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.userData.set(response.data);
          } else {
            this.cancelled.emit();
          }
        },
        error: () => this.cancelled.emit(),
      });
  }

  protected onSave(formValue: Partial<UserUpdate>): void {
    this.isLoading.set(true);
    const payload: Partial<UserUpdate> = {...formValue, id: this.userId};
    this.userService
      .updateMyUserData(payload as UserUpdate)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.alertService.showSuccess(Messages.USER_UPDATED_SUCCESS);
            this.saved.emit(response.data);
          }
        },
      });
  }
}
